import type { AppProps } from 'next/app'
import Head from 'next/head'
import Script from 'next/script'
import { appWithTranslation, useTranslation } from 'next-i18next'
import { ApolloProvider } from '@apollo/client'
import * as Sentry from '@sentry/react'
import ErrorPage from '@/pages/_error'
import SiteLayout from '@/components/SiteLayout'
import useApollo from '@hooks/useApollo'
import AuthContext, { useProvideAuth } from '@/hooks/useAuth'
import EnvContext, { useEnvProvider } from '@/hooks/useEnv'
import ProfileContext, { useProfileContent } from '@/hooks/useProfile'
import TitleBarContext, { useTitleBarInfo } from '@/hooks/useTitleBarSize'
import { ToastContext, useToastStates } from '@/hooks/useToast'
import { TooltipContext, useTooltipStates } from '@/hooks/useTooltip'
import useServiceFromRouter from '@/hooks/useServiceFromRouter'
import TrackingContext from '@/lib/tracking/TrackingContext'
import { useInitTracking } from '@/lib/tracking/useTracking'
import UserAgent from '@utils/userAgent'
import ContextProviderComposer from '@/utils/contextProviderComposer'
import config from '@utils/config'
import '@/styles/index.sass'
import { TrackingInfoContext, useTrackingInfoProvider } from '@/hooks/useTrackingInfo'

const { publicRuntimeConfig } = config
if (publicRuntimeConfig.SENTRY_DSN) {
  Sentry.init({
    dsn: publicRuntimeConfig.SENTRY_DSN,
    environment: publicRuntimeConfig.ENV_PHASE,
    tracesSampleRate: 1.0
  })
}

function getLiffId () {
  if (typeof liff === 'undefined') return null
  let liffId = window.sessionStorage.getItem('liffId')
  if (!liffId) {
    const { publicRuntimeConfig: { LIFF_ID } } = config
    liffId = LIFF_ID
  }
  // Save the liff id. In case when navigating to a 3rd-party webpage in the current liff window,
  // we can re-init the liff after navigating back from the 3rd-party webpage.
  window.sessionStorage.setItem('liffId', liffId)
  return liffId
}

function initLiff () {
  if (!liff) return
  const liffId = getLiffId()
  if (!liffId) return

  liff.init({ liffId })
}

function MyApp ({ Component, pageProps }: AppProps) {
  const _service = useServiceFromRouter()
  const {
    publicRuntimeConfig: {
      UTS_SDK_URL,
      LIFF_SDK_URL,
      TRACKING_CONFIGS: trackerConfigs,
      UTS_IDS: utsIds,
      ENABLE_DEBUG_FEATURE: debug
    }
  } = config
  const { t } = useTranslation()
  const pageTitle = t('client.cart.title.yourcart')
  const client = useApollo(pageProps)
  const userAgent = new UserAgent()
  const trackingOptions = {
    debug,
    trackerConfigs,
    utsId: utsIds[_service]
  }
  const { meta, ...restProps } = pageProps as any
  return (
    <Sentry.ErrorBoundary fallback={ErrorPage}>
      <Script id="uts-sdk" src={UTS_SDK_URL} strategy="beforeInteractive" />
      <Script id="liff-sdk" src={LIFF_SDK_URL} strategy="beforeInteractive" onReady={initLiff} />
      <ApolloProvider client={client}>
        <ContextProviderComposer
          contextProviders={[
            <TrackingContext.Provider key="tracking" value={useInitTracking(trackingOptions)} />,
            <EnvContext.Provider key="env" value={useEnvProvider(userAgent)} />, // client-only for now
            <ProfileContext.Provider key="profile" value={useProfileContent(client)} />,
            <AuthContext.Provider key="auth" value={useProvideAuth()} />,
            <TitleBarContext.Provider key="footer" value={useTitleBarInfo()} />,
            <ToastContext.Provider key="toast" value={useToastStates()} />,
            <TooltipContext.Provider key="tooltip" value={useTooltipStates()} />,
            <TrackingInfoContext.Provider key="trackingInfo" value={useTrackingInfoProvider(meta?.trackingInfo)} />
          ]}
        >
          <>
            <Head>
              <meta
                name="viewport"
                content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0, viewport-fit=cover"
              />
              <title>{pageTitle}</title>
            </Head>
            <SiteLayout authRequired={meta?.authRequired}>
              <Component {...restProps} />
            </SiteLayout>
          </>
        </ContextProviderComposer>
      </ApolloProvider>
    </Sentry.ErrorBoundary>
  )
}

export default appWithTranslation(MyApp)
